import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { adminApi } from '../services/api.jsx';

/* ═══════════════════════════════════════════════════════════════════════
   LES DROITS DU COMPTE CONNECTÉ

   Une seule lecture de `/users/me/zones/`, partagée par tout ce qui en
   dépend : le menu, le garde de route et les boutons d'export. Chacun
   l'interrogeait de son côté — trois requêtes pour une réponse, et trois
   occasions de diverger si l'une d'elles n'était pas rafraîchie.

   Ce que cette réponse contient, et ce qu'elle ne remplace pas
   ------------------------------------------------------------
   Elle donne, pour chaque zone ouverte au compte, les quatre droits :
   voir, ajouter, modifier, exporter. L'interface s'en sert pour ne pas
   MONTRER ce qui sera refusé — masquer un bouton d'export plutôt que le
   laisser échouer au clic.

   Ce n'est pas une protection. Le serveur refuse de lui-même, et c'est
   lui qui fait foi : ici, on évite seulement de promettre ce qu'on ne
   peut pas tenir.
   ═══════════════════════════════════════════════════════════════════════ */

const Contexte = createContext(null);

export function FournisseurDroits({ children }) {
  const [zones, setZones] = useState(null);   // null = pas encore su
  const [erreur, setErreur] = useState(false);

  const charger = useCallback(() => {
    adminApi.mesZones()
      .then((d) => { setZones(Array.isArray(d?.zones) ? d.zones : []); setErreur(false); })
      .catch(() => { setZones([]); setErreur(true); });
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const valeur = useMemo(() => {
    const parCle = new Map((zones || []).map((z) => [z.cle, z]));

    /* Du chemin vers la zone. Les chemins déclarés côté serveur sont des
       préfixes : « /users » couvre aussi « /users/EBOJ9 ». On retient le
       plus long qui corresponde, sans quoi « / » les capturerait tous. */
    const zoneDuChemin = (chemin) => {
      let trouvee = null;
      (zones || []).forEach((z) => {
        if (!z.chemin || z.chemin === '/') return;
        if (chemin === z.chemin || chemin.startsWith(`${z.chemin}/`)) {
          if (!trouvee || z.chemin.length > trouvee.chemin.length) trouvee = z;
        }
      });
      return trouvee;
    };

    return {
      zones: zones || [],
      pretes: zones !== null,
      erreur,
      recharger: charger,
      /* `peut('paiements', 'exporter')`. Tant que la réponse n'est pas
         arrivée on répond `false` : mieux vaut un bouton qui apparaît
         avec un instant de retard qu'un bouton qui disparaît sous le
         curseur une fois la réponse reçue. */
      peut: (cle, action = 'voir') => Boolean(parCle.get(cle)?.[action]),
      zoneDuChemin,
    };
  }, [zones, erreur, charger]);

  return <Contexte.Provider value={valeur}>{children}</Contexte.Provider>;
}

export function useDroits() {
  const v = useContext(Contexte);
  if (!v) {
    // Hors du fournisseur, on répond « rien d'ouvert » plutôt que de
    // lever : un composant isolé dans un test ne doit pas casser.
    return {
      zones: [], pretes: false, erreur: false,
      peut: () => false, zoneDuChemin: () => null, recharger: () => {},
    };
  }
  return v;
}
